import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogData } from "../Blog/BlogData";
import { Calendar, Clock, ChevronRight, ChevronLeft, ArrowRight } from "lucide-react";

const Blogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  const sortedBlogs = [...blogData].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = sortedBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(sortedBlogs.length / blogsPerPage);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  return (
    // Body ko dark theme diya gaya hai
    <div className="bg-[#0f0f0f] min-h-screen font-serif text-[#d4af37]">
      {/* --- HERO SECTION --- */}
      <div className="pt-44 pb-20 border-b border-[#2a2a2a] bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex justify-center items-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-[#c5a059]"></span>
            <span className="text-[#c5a059] uppercase tracking-[0.4em] text-xs font-bold font-sans">
              Legal Insights
            </span>
            <span className="h-[1px] w-10 bg-[#c5a059]"></span>
          </div>
          <h1 className="text-5xl md:text-7xl font-medium tracking-tight mb-6 text-white">
            The <span className="italic font-light text-[#c5a059]">Advocate's</span> Journal
          </h1>
          <p className="max-w-xl mx-auto text-gray-400 font-sans text-lg leading-relaxed">
            Expert analysis on the latest legal precedents, corporate laws, and 
            strategic case studies.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-24">
        {/* --- BLOG GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-20">
          {currentBlogs.map((blog) => (
            <article key={blog.id} className="group flex flex-col h-full">
              <Link to={`/blogs/${blog.id}`} className="flex flex-col h-full">
                
                {/* Image Container with Gold Border on Hover */}
                <div className="relative aspect-[16/10] overflow-hidden mb-8 bg-[#1a1a1a] ring-1 ring-white/10 group-hover:ring-[#c5a059] transition-all duration-500 shadow-2xl">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700 ease-in-out opacity-80 group-hover:opacity-100"
                  />
                  <div className="absolute top-0 left-0 bg-[#c5a059] text-black px-4 py-2 text-[10px] font-sans font-bold uppercase tracking-widest">
                    {blog.category}
                  </div>
                </div>

                {/* Metadata - Softened Gold */}
                <div className="flex items-center gap-4 text-[11px] font-sans uppercase tracking-widest text-[#a88d58] mb-4">
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} />
                    {new Date(blog.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock size={13} />
                    6 Min Read
                  </span>
                </div>

                {/* Content - White Title, Golden-Brown Text */}
                <div className="flex-grow">
                  <h3 className="text-2xl font-semibold mb-4 leading-tight text-white group-hover:text-[#c5a059] transition-colors duration-300">
                    {blog.title}
                  </h3>
                  <p className="text-[#a18a68] font-sans text-[15px] leading-relaxed line-clamp-3 mb-8">
                    {blog.excerpt}
                  </p>
                </div>

                {/* Bottom CTA - Premium Styling */}
                <div className="pt-6 border-t border-[#2a2a2a] flex items-center justify-between group-hover:border-[#c5a059]/50 transition-colors">
                  <span className="text-xs font-bold font-sans uppercase tracking-[0.2em] text-[#c5a059]">
                    Read Full Case
                  </span>
                  <div className="w-10 h-10 rounded-full border border-[#c5a059]/30 flex items-center justify-center text-[#c5a059] group-hover:bg-[#c5a059] group-hover:text-black transition-all duration-300">
                    <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>

        {/* --- PREMIUM PAGINATION (Dark Theme) --- */}
        {totalPages > 1 && (
          <div className="mt-32 flex justify-center items-center">
            <div className="flex items-center bg-[#1a1a1a] border border-[#2a2a2a] shadow-2xl p-2 rounded-full gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`p-3 rounded-full transition-all ${
                  currentPage === 1 ? "text-gray-700 cursor-not-allowed" : "text-[#c5a059] hover:bg-[#2a2a2a]"
                }`}
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`w-11 h-11 rounded-full font-sans text-sm font-bold transition-all ${
                      currentPage === index + 1
                        ? "bg-[#c5a059] text-black shadow-lg shadow-[#c5a059]/20"
                        : "text-gray-500 hover:text-[#c5a059] hover:bg-[#2a2a2a]"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-3 rounded-full transition-all ${
                  currentPage === totalPages ? "text-gray-700 cursor-not-allowed" : "text-[#c5a059] hover:bg-[#2a2a2a]"
                }`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;